import { useEffect, useState, useReducer } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Components
import Spinner from "../Components/Spinner";
import DeleteModal from "../Components/Modals/Delete-modal";
import ToastError from "../Components/ToastError";

import { FaEdit, FaPlus } from 'react-icons/fa';
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { Filters } from "../../components/Filters";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { vehicleService } from "../../Services/vehicle.service";
import { configsService } from "../../Services/configs.service";
import { BiEditAlt, BiCog, BiShoppingBag } from "react-icons/bi";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
} from "@syncfusion/ej2-react-grids";

const formInitialValues = {
  id: 0,
  name: "",
  isDefault: "",
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function Groups() {
  const { t } = useTranslation();
  const [formData, setFormData] = useReducer(formReducer, {});
  const [isLoading, setIsLoading] = useState(false);
  const [groupsList, setGroupsList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });
  const [editingItem, setEditingItem] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [showToastConclict, setshowToastConclict] = useState(false);
  const navigate = useNavigate();
  const [showCarShoppingModal, setShowCarShoppingModal] = useState(false);
  
  const [groupsConfigs, setGroupsConfigs] = useState(null);
  const [dataGroup, setDataGroup] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [textDelete, setTextDelete] = useState(""); 

  const normalizeGroupItem = (item) => ({
    vehicleGroupId: item?.vehicleGroupId,
    vehicleGroupName: item?.vehicleGroupName,
    isDefaultGroup: item?.isDefaultGroup,
    standardFlag: item?.standardFlag,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({ type: "title", value: t("GROUPS.PAGE_TITLE") });
  }, [dispatch, t]);

  const initialize = async (isUpdate) => {
    try {
      if (!isUpdate) setIsLoading(true);
      const response = await vehicleGroupService.fetchGroups();
      setGroupsList(response || []);
    } catch (error) {
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };

  const onEdit = (item) => {
    const normalizedItem = normalizeGroupItem(item);
    setEditingItem(normalizedItem);

    setFormData({
      name: "name",
      value: normalizedItem.vehicleGroupName,
    });

    setFormData({
      name: "id",
      value: normalizedItem.vehicleGroupId,
    });

    setFormData({
      name: "isDefault",
      value: normalizedItem.isDefaultGroup,
    });

    setShowForm(true);
  };

  const onConfig = (item) => {
    const normalizedItem = normalizeGroupItem(item);
    navigate("/vehicles/configuration", { state: { item: normalizedItem } });
  };

  const onCarShopping = (item) => {
    const normalizedItem = normalizeGroupItem(item);
    fetchVehicleGroups(normalizedItem);
    setShowCarShoppingModal(true);
  };

  const fetchVehicleGroups = async (item) => {
    try {
      setFiltersLoading(true);
      const groups = await vehicleService.fetchGroups();
      const groupsConfigs = await configsService.fetchGroupsConfigs(item.vehicleGroupId);

      setDataGroup(groups);
      setGroupsConfigs(groupsConfigs);
    } catch (error) {
      console.log(error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const onNew = () => {
    setFormData({
      reset: true,
    });
    setNewGroup(true);
    setShowForm(true);
    setEditingItem({});
  };

  const onCancel = () => {
    setFormData({
      reset: true,
    });
    setEditingItem({});
    setNewGroup(false);
    setShowForm(false);
  };

  const onDelete = (item) => {
    const normalizedItem = normalizeGroupItem(item);
    setIsDeleting({
      show: true,
      itemName: formData.name,
      itemRemoveId: formData.id,
    });
    setEditingItem(normalizedItem);
  };

  const onDefineDefault = async () => {
    if (formData.id) {
      try {
        setIsLoading(true);

        await vehicleGroupService.defineDefault(formData.id);
        await initialize(true);
      } catch (error) {
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setNewGroup(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  const handleRemove = async (id) => {
    if (id) {
      try {
        setIsLoading(true);
        let response = await vehicleGroupService.remove(id);

        if (response?.status === 409) {
          setshowToastConclict(true);
        }
        if(typeof response === "string"){
          setTextDelete(response)
          setShowToast(true);
        }
        await initialize(true);
      } catch (error) {
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setNewGroup(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  const onCloseToast = () => {
    setShowToast(false);
  };

  const onCloseToastConflit = () => {
    setshowToastConclict(false);
  };

  const onSave = async () => {
    if (formData.name) {
      setIsLoading(true);
      setTextDelete(null);

      const payload = {
        Description: formData.name,
        IsDefault: formData.isDefault ? formData.isDefault : "S",
      };

      try {
        setIsLoading(true);

        if (formData.id) payload["id"] = Number(formData.id || 0);

        let response = "";
        if (payload.id > 0){
          response = await vehicleGroupService.update(payload);
        } else{
          response = await vehicleGroupService.create(payload);
        } 
        
        await initialize(true);

        if (response == null) setShowToast(true);

        if (editingItem && response) {
          editingItem.vehicleGroupName = payload.Description;
          editingItem.isDefaultGroup = payload.IsDefault;
        }
      } catch (error) {
        console.log(error);
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setNewGroup(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value ?? 0;
    setSelectedGroupId(selectedValue);

    if (selectedValue) {
      fetchVehicleGroups({ vehicleGroupId: selectedValue });
    }
  };

  return (
    <>
      <div className="container-xxl flex-grow-1 container-p-y">
        {isLoading ? <Spinner /> : ""}

        <ToastError show={showToast} text={textDelete} closeToast={onCloseToast} />
        <ToastError
          show={showToastConclict}
          title={t("GROUPS.DELETE_ERROR_TITLE")}
          text={textDelete || t("GROUPS.DELETE_ERROR_TEXT")}
          closeToast={onCloseToastConflit}
        />

        <DeleteModal
          itemName={isDeleting.itemName}
          itemRemoveId={isDeleting.itemRemoveId}
          callBackRemove={handleRemove}
          show={isDeleting.show}
          handleClose={handleCloseModal}
        />

        {/* Modal Car Shopping */}
        <Modal
          show={showCarShoppingModal}
          onHide={() => setShowCarShoppingModal(false)}
          centered
        >
          <Modal.Body>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {t("GROUPS.FILTERS")}:
              <select
                className="filter-select form-select-sm"
                value={selectedGroupId || ""}
                onChange={handleSelectChange}
              >
                <option value="">{t("GROUPS.SELECT_GROUP")}</option>
                {dataGroup &&
                  dataGroup.map((g) => (
                    <option key={g.vehicleGroupId} value={g.vehicleGroupId}>
                      {g.vehicleGroupName}
                    </option>
                  ))}
              </select>
            </div>

            <div style={{ marginTop: "20px" }}>
              <div className="table-responsive">
                <table className="table table-striped table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{t("COMMON.PLATE")}</th>
                      <th>{t("GROUPS.CURRENT_GROUP")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupsConfigs && groupsConfigs.length > 0 ? (
                      groupsConfigs.map((vehicle, index) => (
                        <tr key={index}>
                          <td>{vehicle.plate}</td>
                          <td>
                            <select
                              className="filter-select form-select-sm"
                              value={vehicle.selectedGroupId || ""}
                              onChange={(e) => handleSelectChange(index, e.target.value)}
                            >
                              <option value="">{t("GROUPS.SELECT_GROUP")}</option>
                              {dataGroup &&
                                dataGroup.map((g) => (
                                  <option key={g.vehicleGroupId} value={g.vehicleGroupId}>
                                    {g.vehicleGroupName}
                                  </option>
                                ))}
                            </select>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center">
                          {t("GROUPS.NO_VEHICLES_FOUND")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCarShoppingModal(false)}>
              {t("BUTTONS.CLOSE")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                alert(t("GROUPS.ACTION_CONFIRMED"));
                setShowCarShoppingModal(false);
              }}
            >
              {t("GROUPS.CONFIRM")}
            </Button>
          </Modal.Footer>
        </Modal>
        
        <div
          className="card"
          style={showForm || isLoading ? { display: "none" } : { display: "" }}
        >
          <div className="d-flex justify-content-between">
            <div className="p-2 card-header">
              <h5>{t("GROUPS.LIST_TITLE")}</h5>
            </div>
            <div className="p-2">
              <button className="btn btn-success btn-sm" onClick={onNew}>
                <FaPlus size={20} />
              </button>
            </div>
          </div>

          <GridComponent
            dataSource={groupsList}
            allowPaging={true}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            pageSettings={{ pageSize: 10 }}
            rowHeight={30}
            height={"auto"}
            locale="pt-BR"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="vehicleGroupName"
                headerText={t("COMMON.DESCRIPTION")}
                width="250"
              />
              <ColumnDirective
                field="standardFlag"
                headerText={t("TABLE.DEFAULT")}
                width="120"
                template={(props) => (
                  <span>
                    {props.standardFlag === "S"
                      ? t("CLIENT_DETAILS.YES")
                      : props.standardFlag === "N"
                      ? t("CLIENT_DETAILS.NO")
                      : props.standardFlag}
                  </span>
                )}
              />
              <ColumnDirective
                headerText={t("TABLE.ACTIONS")}
                width="170"
                template={(props) => (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <BiEditAlt
                      className="icon-button"
                      onClick={() => onEdit(props)}
                    />
                    <BiCog
                      className="icon-button"
                      onClick={() => onConfig(props)}
                    />
                    <BiShoppingBag
                      className="icon-button"
                      onClick={() => onCarShopping(props)}
                    />
                  </div>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter]} />
          </GridComponent>
        </div>

        <div
          className="row py-3"
          style={
            !showForm || isLoading ? { display: "none" } : { display: "block" }
          }
        >
          <div className="col-6">
            <div className="card">
              <h5 className="card-header">{t("GROUPS.REGISTER_TITLE")}</h5>
              <div className="card-header">
                <div className="row mb-3">
                  <label
                    className="col-sm-2 col-form-label"
                    htmlFor="basic-default-name"
                  >
                    {t("COMMON.NAME")}
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder={t("COMMON.NAME")}
                      value={formData.name || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div
                  className="row justify-content-end"
                  style={{ textAlign: "end" }}
                >
                  <div className="col-lg-12 col-md-12 col-xs-12 mb-3 d-flex flex-wrap justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={onCancel}
                    >
                      {t("BUTTONS.CLOSE")}
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSave}
                      disabled={isLoading}
                    >
                      {t("BUTTONS.SAVE")}
                    </button>

                    <button
                      style={
                        newGroup ? { display: "none" } : { display: "block" }
                      }
                      type="button"
                      className="btn btn-success"
                      onClick={onDefineDefault}
                      disabled={isLoading}
                    >
                      {t("GROUPS.DEFINE_DEFAULT")}
                    </button>

                    <button
                      style={
                        newGroup ? { display: "none" } : { display: "block" }
                      }
                      type="button"
                      className="btn btn-danger"
                      onClick={onDelete}
                      disabled={isLoading}
                    >
                      {t("BUTTONS.DELETE")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  );
}